import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useContentAnalytics } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Professional color palette for features
const FEATURE_COLORS: Record<string, string> = {
  public_prompts: '#3b82f6',      // blue
  private_prompts: '#8b5cf6',     // violet
  collections: '#10b981',         // emerald
  comments: '#f59e0b',            // amber
  votes: '#ef4444',               // red
};

// Convert DateRange to days
function dateRangeToDays(dateRange: DateRange): number | undefined {
  const match = dateRange.start.match(/^-(\d+)d$/);
  if (match) return parseInt(match[1], 10);
  return undefined; // all time
}

// Feature key display names
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  public_prompts: 'Public Prompts',
  private_prompts: 'Private Prompts',
  collections: 'Collections',
  comments: 'Comments',
  votes: 'Votes',
};

interface Props {
  dateRange: DateRange;
}

/**
 * Top Features Chart
 * Professional visualization of most used features by usage count
 */
export function TopFeaturesChart({ dateRange }: Props) {
  const days = dateRangeToDays(dateRange);
  const { data, isLoading, error } = useContentAnalytics(days);

  const chartData = (data || []).map((item) => ({
    ...item,
    name: FEATURE_DISPLAY_NAMES[item.feature_key] || item.feature_key.replace(/_/g, ' ').toUpperCase(),
    displayColor: FEATURE_COLORS[item.feature_key] || '#6b7280',
  }));

  // Calculate max usage for consistent bar sizing
  const maxUsage = Math.max(...chartData.map(d => d.usage_count), 0);

  const FeaturesIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  return (
    <Card
      title="Most Used Features"
      subtitle="Feature adoption and usage patterns"
      isLoading={isLoading}
      error={error?.message || null}
      icon={FeaturesIcon}
      accentColor="from-amber-500 to-orange-500"
    >
      {chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm font-medium">No feature usage data</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 140, bottom: 5 }}
            >
              <defs>
                {chartData.map((item, idx) => (
                  <linearGradient key={`gradient-${idx}`} id={`feature-gradient-${idx}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={item.displayColor} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={item.displayColor} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={140}
                tick={{ fontSize: 12, fill: '#1e293b', fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: any) => {
                  if (typeof value === 'number') {
                    return [value.toLocaleString(), 'Usage Count'];
                  }
                  return value;
                }}
                contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
                  padding: '12px 16px',
                }}
              />
              <Bar dataKey="usage_count" radius={[0, 12, 12, 0]} barSize={32}>
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={`url(#feature-gradient-${index})`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Feature breakdown with percentages */}
          <div className="mt-6 space-y-2">
            {chartData.map((item: any) => {
              const percentage = (item.usage_count / maxUsage) * 100;
              return (
                <div key={item.feature_key} className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.displayColor }}
                      />
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {item.usage_count.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 group-hover:shadow-lg"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.displayColor,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {percentage.toFixed(1)}% of total usage
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}
